import { BaseSyntheticEvent, useCallback, useMemo, useState } from "react";
import { Loader } from "../../components/ui/Loader";
import { DataTable } from "primereact/datatable";
import { TableHeader } from "../../components/ui/table-header";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { useToast } from "../../components/ui/toast-context-provider";
import { useQueryClient } from "react-query";
import Enumerable from "linq";
import {
  CustomModal,
  CustomModalProps,
} from "../../components/ui/MobileModal/custom-modal";
import { FilterMatchMode } from "primereact/api";
import { SkillLevelForm } from "./SkillLevelsForm";
import {
  useAddSkillLevelMutation,
  useDeleteSkillLevelMutation,
  useEditSkillLevelMutation,
  useSkillLevelsQuery,
} from "../../queries/skill-levels.query";
import { SkillLevel } from "../../queries/models/skill-level.model";
import { authService } from "../../services/auth.service";

export function SkillLevels() {
  const isAdmin = useMemo(() => {
    return authService.isAdmin();
  }, []);
  const queryClient = useQueryClient();
  const skillLevelsQuery = useSkillLevelsQuery();
  const addSkillLevelMutation = useAddSkillLevelMutation();
  const editSkillLevelMutation = useEditSkillLevelMutation();
  const deleteSkillLevelMutation = useDeleteSkillLevelMutation();
  const toast = useToast();
  const [selectedSkillLevel, setSelectedSkillLevel] = useState<SkillLevel>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState<boolean>(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const handleGlobalFilterChange = useCallback(
    (e: BaseSyntheticEvent) => {
      const value = e.target.value;
      let _filters = filters;
      _filters.global.value = value;

      setFilters(_filters);
      setGlobalFilterValue(value);
    },
    [filters]
  );

  const handleAddNewSkillLevel = () => {
    setSelectedSkillLevel({
      name: "",
    } as SkillLevel);
  };

  const handleSaveSkillLevel = (data: SkillLevel) => {
    return data?.id ? editExistingSkillLevel(data) : saveNewSkillLevel(data);
  };

  const saveNewSkillLevel = (data: SkillLevel) => {
    setIsSubmitting(true);
    const mutateOptions = {
      onSuccess: async () => {
        setSelectedSkillLevel(undefined);
        toast.current?.show({
          severity: "success",
          detail: "Zapisano!",
        });
        await queryClient.invalidateQueries();
      },
      onError: async (error: any) => {
        toast.current?.show({
          severity: "error",
          detail: error.message,
        });
      },
      onSettled: () => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      },
    };

    const request: SkillLevel = {
      id: data.id,
      name: data.name,
      order: data.order,
      comment: data.comment,
    };

    return addSkillLevelMutation.mutateAsync(request, mutateOptions);
  };

  const editExistingSkillLevel = (data: SkillLevel) => {
    setIsSubmitting(true);
    const mutateOptions = {
      onSuccess: async () => {
        setSelectedSkillLevel(undefined);
        toast.current?.show({
          severity: "success",
          detail: "Zapisano!",
        });
        await queryClient.invalidateQueries();
      },
      onError: async (error: any) => {
        toast.current?.show({
          severity: "error",
          detail: error.message,
        });
      },
      onSettled: () => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      },
    };

    const request: SkillLevel = {
      id: data.id,
      name: data.name,
      order: data.order,
      comment: data.comment,
    };

    return editSkillLevelMutation.mutateAsync(request, mutateOptions);
  };

  const mobileSkillLevelFormModalProps: CustomModalProps = {
    header: selectedSkillLevel?.id
      ? selectedSkillLevel.name
      : "Dodaj nowy poziom",
    onClose: () => {
      setSelectedSkillLevel(undefined);
      setIsModalOpen(false);
    },
    isOpen: isModalOpen,
    body: (
      <SkillLevelForm
        skillLevel={selectedSkillLevel ?? ({} as SkillLevel)}
        onSave={handleSaveSkillLevel}
        onCancel={() => {
          setSelectedSkillLevel(undefined);
          setIsModalOpen(false);
        }}
        onDelete={() => setIsDeleteConfirmationModalOpen(true)}
        isSubmitting={isSubmitting}
      />
    ),
  };

  const handleDeleteSkillLevel = useCallback(() => {
    if (selectedSkillLevel) {
      deleteSkillLevelMutation.mutateAsync(selectedSkillLevel.id, {
        onSuccess: async () => {
          await queryClient.invalidateQueries();
          toast.current?.show({
            severity: "success",
            detail: "Zapisano!",
          });
          setSelectedSkillLevel(undefined);
          setIsDeleteConfirmationModalOpen(false);
          setIsModalOpen(false);
        },
        onError: async (error: any) => {
          toast.current?.show({
            severity: "error",
            detail: error.message,
          });
        },
      });
    }
  }, [deleteSkillLevelMutation, queryClient, selectedSkillLevel, toast]);

  const mobileModalConfirmationFormProps: CustomModalProps = {
    header: "Potwierdź",
    body: "Czy chcesz usunąć?",
    height: "160px",
    width: "50%",
    isOpen: isDeleteConfirmationModalOpen,
    confirmation: true,
    onClose: () => setIsDeleteConfirmationModalOpen(false),
    centered: true,
    justified: true,
    onConfirm: handleDeleteSkillLevel,
  };

  return (
    <>
      {skillLevelsQuery.isLoading ? (
        <div className="h-full flex align-items-center">
          <Loader type="spinner" />
        </div>
      ) : (
        <div className="h-full">
          {!!mobileSkillLevelFormModalProps && (
            <CustomModal {...mobileSkillLevelFormModalProps} />
          )}
          {!!mobileModalConfirmationFormProps && (
            <CustomModal {...mobileModalConfirmationFormProps} />
          )}
          <div className="p-1 ">
            <InputText
              className="w-full"
              placeholder="Filtr globalny"
              value={globalFilterValue}
              onChange={handleGlobalFilterChange}
            />
          </div>

          <div style={{ height: "calc(100% - 3rem)" }}>
            <DataTable
              value={skillLevelsQuery.data}
              resizableColumns={true}
              scrollable={true}
              scrollHeight="flex"
              selectionMode="single"
              selection={selectedSkillLevel}
              onSelectionChange={(e) => {
                setSelectedSkillLevel(e.value as SkillLevel);
                setIsModalOpen(true);
              }}
              filters={filters}
              globalFilterFields={["name", "comment"]}
              header={
                <TableHeader
                  header="Poziomy umiejętności"
                  showAddButton={isAdmin}
                  onClickAdd={() => {
                    handleAddNewSkillLevel();
                    setIsModalOpen(true);
                  }}
                />
              }
            >
              <Column field="order" header="Kolejność" sortable />
              <Column field="name" header="Nazwa" sortable />
            </DataTable>
          </div>
        </div>
      )}
    </>
  );
}
